/**
 * ProposalTypeFormDialog - Modal de criação/edição de Tipo de Proposta
 *
 * Espelha o LossReasonFormDialog (mesma validação Zod, mesmo layout) para
 * manter a consistência visual dos parâmetros do CRM.
 *
 * RELEASE: 2026-08-25 v1.0.0
 */
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { FileText, Loader2 } from 'lucide-react';
import {
  useUpsertProposalType,
  type ProposalTypeEntry,
} from '@/hooks/useCrmProposalTypes';

// Validação de entrada (limites alinhados ao backend)
const schema = z.object({
  name: z.string().trim().min(1, 'Informe um nome').max(100),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  color: z
    .string()
    .trim()
    .regex(/^#([0-9a-fA-F]{6})$/, 'Use cor no formato #RRGGBB')
    .optional()
    .or(z.literal('')),
  sort_order: z.number().int().min(0).max(9999).optional(),
  is_active: z.boolean().optional(),
});

interface ProposalTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: ProposalTypeEntry | null;
  onSaved?: (id: string) => void;
}

export function ProposalTypeFormDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: ProposalTypeFormDialogProps) {
  const upsert = useUpsertProposalType();

  // Estado local do formulário
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Sempre que o modal reabre, sincroniza com a entrada de edição
  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setDescription(editing?.description ?? '');
      setColor(editing?.color ?? '#3b82f6');
      setSortOrder(editing?.sort_order ?? 0);
      setIsActive(editing?.is_active ?? true);
      setErrors({});
    }
  }, [open, editing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({
      name,
      description,
      color,
      sort_order: sortOrder,
      is_active: isActive,
    });
    if (!parsed.success) {
      const fieldErr: Record<string, string> = {};
      parsed.error.errors.forEach((er) => {
        fieldErr[er.path[0] as string] = er.message;
      });
      setErrors(fieldErr);
      return;
    }

    const id = await upsert.mutateAsync({
      id: editing?.id ?? null,
      name: parsed.data.name,
      description: parsed.data.description || null,
      color: parsed.data.color || null,
      sort_order: parsed.data.sort_order ?? 0,
      is_active: parsed.data.is_active ?? true,
    });

    onSaved?.(id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>
                {editing ? 'Editar tipo de proposta' : 'Novo tipo de proposta'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Tipos usados ao gerar uma proposta (ex.: Nova venda, Transferência).
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pt-name">Nome *</Label>
            <Input
              id="pt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Ex.: Nova venda"
              autoFocus
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pt-desc">Descrição</Label>
            <Textarea
              id="pt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Contexto ou orientação para os vendedores"
            />
            {errors.description && (
              <p className="text-xs text-destructive">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pt-color">Cor</Label>
              <div className="flex items-center gap-2">
                <input
                  id="pt-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 rounded border border-input cursor-pointer bg-background"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#3b82f6"
                />
              </div>
              {errors.color && <p className="text-xs text-destructive">{errors.color}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-order">Ordem</Label>
              <Input
                id="pt-order"
                type="number"
                min={0}
                max={9999}
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="pt-active" className="cursor-pointer">
                Ativo
              </Label>
              <p className="text-xs text-muted-foreground">
                Tipos inativos não aparecem na seleção ao gerar uma proposta.
              </p>
            </div>
            <Switch id="pt-active" checked={isActive} onCheckedChange={setIsActive} />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={upsert.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              {editing ? 'Salvar alterações' : 'Criar tipo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
